import React, { useState } from 'react';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    CircularProgress,
    TextField,
    Typography,
    Stack,
    Divider,
    List, ListItem, ListItemText, ListItemIcon,
    ButtonGroup
} from '@mui/material';
import api from '../api';
import SecurityIcon from '@mui/icons-material/Security';
import { useI18n } from '../hooks/useI18n';
import QrCodeIcon from '@mui/icons-material/QrCode';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const SetupStep = ({ stepIcon, stepPrimary, stepSecondary, stepAction }) => {
    return (
        <React.Fragment>
            <ListItem alignItems='flex-center'>
                <ListItemIcon sx={{ mr: 1, minWidth: '40px', color: 'primary.main', pl: 1 }}>
                    {stepIcon}
                </ListItemIcon>
                <ListItemText
                    sx={{ borderLeft: '1px solid', borderColor: 'secondary.main', pl: 2 }}
                    primary={
                        <Typography variant="subtitle1" component="span" fontWeight="bold">
                            {stepPrimary}
                        </Typography>
                    }
                    secondary={
                        <React.Fragment>
                            <Typography
                                component="span"
                                variant="caption"
                                sx={{ display: 'inline' }}
                            >
                                {stepSecondary}
                            </Typography>
                            {stepAction && <Box sx={{ mt: 1 }}>{stepAction}</Box>}
                        </React.Fragment>
                    }
                />
            </ListItem>
        </React.Fragment>
    );
}

const TwoFactorAuthSetup = ({ onSuccess }) => {
    const { t } = useI18n('auth');
    const [setupData, setSetupData] = useState(null);
    const [otpCode, setOtpCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleEnableClick = async () => {
        setIsLoading(true);
        setError('');
        setSuccess('');
        try {
            const response = await api.post('/users/auth/totp/setup/');
            setSetupData(response.data);
        } catch (err) {
            setError(err.response?.data?.detail || t('mfa.messages.setupFailed', { defaultValue: 'Impossibile iniziare la configurazione 2FA. Riprova più tardi.' }));
        } finally {
            setIsLoading(false);
        }
    };

    const handleConfirmSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setSuccess('');
        if (otpCode.length !== 6 || !/^\d{6}$/.test(otpCode)) {
            setError(t('mfa.messages.invalidCode', { defaultValue: 'Codice di verifica non valido. Riprova' }));
            setIsLoading(false);
            return;
        }
        try {
            await api.post('/users/auth/totp/confirm/', { code: otpCode });
            setSuccess(t('mfa.messages.enabledSuccess', { defaultValue: 'Autenticazione a due fattori abilitata con successo!' }) + ' ' + t('mfa.instructions.usage', { defaultValue: 'Da ora in poi ti verrà richiesto un codice al login. Usa la tua app di autenticazione per generare il codice.' }));
            setSetupData(null);
            if (onSuccess) {
                onSuccess();
            }
        } catch (err) {
            setError(err.response?.data?.detail || t('mfa.messages.confirmFailed', { defaultValue: 'Impossibile confermare il codice. Riprova.' }));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Stack spacing={3}>
            <Typography variant="h5" component="h2">{t('mfa.title', { defaultValue: 'Autenticazione a due fattori (2FA)' })}</Typography>
            <Box mb={2} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Alert severity="info" icon={<SecurityIcon fontSize="inherit" />}>
                    <AlertTitle variant="body2">{t('mfa.labels.protectAccount', { defaultValue: "Proteggi il tuo account con l'autenticazione a due fattori" })}</AlertTitle>
                </Alert>
            </Box>
            <Typography variant="caption" sx={{ px: 2, textAlign: 'justify' }}>
                <strong>{t('mfa.description', { defaultValue: 'L\'autenticazione a due fattori (2FA) aggiunge un ulteriore livello di sicurezza al tuo account' })}
                </strong>
                <br />
                {t('mfa.instructions.usage', { defaultValue: 'Dopo aver abilitato il 2FA, oltre alla tua password, ti verrà richiesto di inserire un codice di verifica generato dalla tua app di autenticazione.' })}
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            {!setupData && (
                <>

                    <Box display="flex" justifyContent="flex-start" alignItems="center" flexDirection={"column"}>
                        <Button variant="contained" onClick={handleEnableClick} disabled={isLoading}>
                            {isLoading ? <CircularProgress size={24} /> : t('mfa.disabled.button', { defaultValue: 'Abilita 2FA' })}
                        </Button>
                    </Box>
                </>
            )}

            {setupData && (
                <Box>
                    <Divider sx={{ m: 2 }} />
                    <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
                        {t('mfa.instructions.title', { defaultValue: 'Istruzioni per la configurazione del 2FA' })}
                    </Typography>
                    <List>
                        <SetupStep
                            stepIcon={<DownloadIcon />}
                            stepPrimary={t('mfa.instructions.steps.step1.title', { defaultValue: '1. Installa un\'app di autenticazione sul tuo dispositivo mobile.' })}
                            stepSecondary={t('mfa.instructions.steps.step1.description', { defaultValue: 'Controlla il tuo dispositivo aziendale per un\'app di autenticazione (ad esempio Google Authenticator, Authy, Microsoft Authenticator) o chiedi assistenza al tuo reparto IT.' })}
                        />
                        <SetupStep
                            stepIcon={<QrCodeIcon />}
                            stepPrimary={t('mfa.instructions.steps.step2.title', { defaultValue: '2. Scansiona il codice QR con l\'app di autenticazione.' })}
                            stepSecondary={t('mfa.instructions.steps.step2.description', { defaultValue: 'Apri l\'app di autenticazione sul tuo dispositivo mobile e usa la funzione di scansione del codice QR per aggiungere un nuovo account.' })}
                            stepAction={
                                <Box sx={{ my: 2, textAlign: 'center' }}>
                                    <img src={setupData.qr_code_base64} style={{ maxWidth: '200px', width: '100%', height: 'auto' }} alt={t('mfa.labels.qrImage', { defaultValue: "Immagine QR per 2FA" })} />
                                </Box>
                            }
                        />

                        <SetupStep
                            lastStep={true}
                            stepIcon={<CheckCircleIcon />}
                            stepPrimary={t('mfa.instructions.steps.step3.title', { defaultValue: '3. Inserisci il codice di verifica generato dall\'app.' })}
                            stepSecondary={t('mfa.instructions.steps.step3.description', { defaultValue: 'Dopo aver scansionato il codice QR, l\'app di autenticazione genererà un codice di verifica a 6 cifre. Inserisci questo codice nel campo sottostante e conferma per completare la configurazione del 2FA.' })}
                            stepAction={
                                <form onSubmit={handleConfirmSubmit}>
                                    <Box display="flex" justifyContent="center" alignItems="center">
                                        <TextField
                                            label={t('mfa.labels.code', { defaultValue: 'Codice di Verifica' })}
                                            value={otpCode}
                                            onChange={(e) => setOtpCode(e.target.value)}
                                            inputProps={{ maxLength: 6 }}
                                            sx={{ mt: 2, mb: 2 }}
                                            required
                                        />
                                    </Box>
                                    <Box display="flex" justifyContent="center" alignItems="center" flexDirection={"row"}>
                                        <ButtonGroup>
                                            <Button 
                                                type="submit"
                                                variant="contained"
                                                color="success"
                                                sx={{ mt: 2 }} disabled={isLoading}>
                                                {isLoading ? 
                                                        <CircularProgress size={24} /> 
                                                    : t('common:messages.confirmAndEnable', { defaultValue: 'Conferma e Attiva' })
                                                }
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outlined"
                                                color="error"
                                                sx={{ mt: 2, ml: 2 }}
                                                onClick={() => setSetupData(null)}
                                                disabled={isLoading}
                                            >
                                                {t('common:buttons.cancel', { defaultValue: 'Annulla' })}
                                            </Button>
                                        </ButtonGroup>
                                    </Box>
                                </form>
                            }
                        />
                    </List>
                </Box>
            )}
        </Stack>
    );
};

export default TwoFactorAuthSetup;